import { useAuth } from "../auth/AuthContext";
import { OFFLINE, problemOf } from "../data/problems";
import { LoadingCards, Message } from "./Status";
import ui from "./ui.module.css";

const TryAgain = ({ onRetry, primary = true }) => (
  <button
    type="button"
    className={`${ui.button} ${primary ? ui.primary : ui.secondary}`}
    onClick={onRetry}
  >
    Try again
  </button>
);

// Preview mode, or a build without the learner gateway.
export const WorkspaceUnavailable = () => (
  <Message title="Learner work isn't available here">
    Learner progress and work appear here once this app is connected to your
    school's learner tablets.
  </Message>
);

// What a workspace screen shows instead of, or above, its snapshot.
// `notFound` explains a 404 for the thing the screen is about.
const WorkspaceStatus = ({ resource, loadingLabel, notFound, className }) => {
  const { signOut } = useAuth();

  if (resource.status === "unavailable") return <WorkspaceUnavailable />;
  if (resource.status === "loading") return <LoadingCards label={loadingLabel} />;
  if (resource.status !== "error") return null;

  if (resource.stale) {
    return (
      <div className={`${ui.notice} ${ui.warning} ${ui.row}`} role="status">
        <p>Can't refresh right now. Showing the last update.</p>
        <button type="button" className={ui.textButton} onClick={resource.retry}>
          Try again
        </button>
      </div>
    );
  }

  const status = resource.error?.response?.status;
  if (problemOf(resource.error) === OFFLINE) {
    return (
      <Message title="You're offline" actions={<TryAgain onRetry={resource.retry} />}>
        This needs a connection. We'll try again when you're back online.
      </Message>
    );
  }
  if (status === 401) {
    return (
      <Message
        title="Please sign in again"
        actions={
          <button type="button" className={`${ui.button} ${ui.primary}`} onClick={signOut}>
            Sign in again
          </button>
        }
      >
        Your sign-in has ended.
      </Message>
    );
  }
  if (status === 403) {
    return (
      <Message title="You can't see this class" actions={<TryAgain onRetry={resource.retry} />}>
        Ask your school to check that you are still linked to {className || "this class"}.
      </Message>
    );
  }
  if (status === 404 && notFound) return notFound;
  return (
    <Message title="This didn't load" actions={<TryAgain onRetry={resource.retry} />}>
      {status >= 400 && status < 500
        ? "Something went wrong. Please try again."
        : "Something went wrong on our side. We'll keep trying, a little less often."}
    </Message>
  );
};

export default WorkspaceStatus;
